import { NextRequest, NextResponse } from 'next/server';
import { getFirebaseAdmin } from '@/lib/firebase-admin';

export async function GET(
    req: NextRequest,
    { params }: { params: Promise<{ productId: string }> }
) {
    try {
        const { productId } = await params;
        const db = getFirebaseAdmin().firestore();

        // jobs 컬렉션에서 인덱싱 이력 조회 (최신순)
        const jobsSnapshot = await db.collection('jobs')
            .where('productId', '==', productId)
            .where('type', '==', 'index_pdf')
            .orderBy('createdAt', 'desc')
            .limit(20)
            .get();

        if (jobsSnapshot.empty) {
            return NextResponse.json({ history: [] });
        }

        // index_logs 컬렉션에서 해당 productId의 품질 지표 조회
        // jobId를 키로 빠르게 참조하기 위해 Map으로 구성
        const jobIds = jobsSnapshot.docs.map((doc: any) => doc.id);

        // Firestore 'in' 쿼리는 최대 30개 제한 — jobIds는 최대 20개이므로 단일 쿼리로 처리 가능
        const indexLogsSnapshot = await db.collection('index_logs')
            .where('jobId', 'in', jobIds)
            .get();

        // jobId → index_log 데이터 맵
        const logMap = new Map<string, any>();
        indexLogsSnapshot.docs.forEach((doc: any) => {
            const data = doc.data();
            if (data.jobId) {
                logMap.set(data.jobId, data);
            }
        });

        // jobs 이력을 시간 순(오래된 것 → 최신)으로 정렬하여 이전 chunksCount 계산에 활용
        const jobsSorted = [...jobsSnapshot.docs].sort((a: any, b: any) => {
            const aTime = a.data().createdAt?.toMillis?.() ?? 0;
            const bTime = b.data().createdAt?.toMillis?.() ?? 0;
            return aTime - bTime; // 오름차순 (오래된 것 먼저)
        });

        // 이전 chunksCount 참조용 맵 구성 (jobId → previousChunksCount)
        const previousChunksMap = new Map<string, number>();
        for (let i = 1; i < jobsSorted.length; i++) {
            const currentJob = jobsSorted[i];
            const prevJob = jobsSorted[i - 1];
            const prevChunks = prevJob.data().chunksCount || 0;
            previousChunksMap.set(currentJob.id, prevChunks);
        }

        // 최신순으로 다시 정렬하여 응답 구성
        const history = jobsSnapshot.docs.map((doc: any) => {
            const data = doc.data();
            const jobId = doc.id;
            const chunksCount = data.chunksCount || 0;
            const log = logMap.get(jobId) ?? null;

            // diff 계산
            // index_logs에 저장된 값이 있으면 우선 사용, 없으면 jobs 이력에서 직접 계산
            let diff: {
                previousChunksCount: number;
                chunksDelta: number;
                addedArticles: number;
                removedArticles: number;
            } | null = null;

            const previousChunksCount = log?.previousChunksCount
                ?? previousChunksMap.get(jobId)
                ?? null;

            if (previousChunksCount !== null) {
                diff = {
                    previousChunksCount,
                    chunksDelta: chunksCount - previousChunksCount,
                    addedArticles: log?.addedArticles ?? 0,
                    removedArticles: log?.removedArticles ?? 0,
                };
            }

            // quality 지표 (index_logs에 기록된 경우에만 포함)
            const quality = log
                ? {
                    avgChunkSize: log.avgChunkSize ?? null,
                    provisoPreservationRate: log.provisoPreservationRate ?? null,
                    switchedAt: log.switchedAt?.toDate?.()?.toISOString?.() ?? null,
                }
                : null;

            return {
                id: jobId,
                status: data.status,
                chunksCount,
                error: data.error || null,
                isReindex: data.isReindex || false,
                blueGreenMode: data.blueGreenMode || false,
                createdAt: data.createdAt?.toDate?.()?.toISOString?.() ?? null,
                updatedAt: data.updatedAt?.toDate?.()?.toISOString?.() ?? null,
                diff,
                quality,
            };
        });

        return NextResponse.json({ history });
    } catch (error: any) {
        return NextResponse.json({ error: error.message }, { status: 500 });
    }
}
